/**
 * Activity — paginated list of all payment links with a status filter.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  downloadInvoicesCsv,
  listInvoicesAdmin,
  type Invoice,
  type InvoiceStatus,
} from "../lib/api";
import { Banner, Btn, Pill } from "../components/ui";
import { Ic } from "../components/Icons";

type ExportRange = "month" | "lastmonth" | "90d" | "year" | "all";
const RANGE_LABELS: Record<ExportRange, string> = {
  month: "This month",
  lastmonth: "Last month",
  "90d": "Last 90 days",
  year: "This year",
  all: "All time",
};
const rangeBounds = (r: ExportRange): { from: number; to: number } => {
  const now = Date.now();
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (r) {
    case "month": return { from: Date.UTC(y, m, 1), to: now };
    case "lastmonth": return { from: Date.UTC(y, m - 1, 1), to: Date.UTC(y, m, 1) };
    case "90d": return { from: now - 90 * 86_400_000, to: now };
    case "year": return { from: Date.UTC(y, 0, 1), to: now };
    case "all": return { from: 0, to: now };
  }
};

const truncMid = (s: string, head = 6, tail = 4): string =>
  s.length <= head + tail + 1 ? s : `${s.slice(0, head)}…${s.slice(-tail)}`;

const formatRelative = (t: number): string => {
  const delta = Date.now() - t;
  const min = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;
  if (delta < min) return "just now";
  if (delta < hour) return `${Math.floor(delta / min)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  return `${Math.floor(delta / day)}d ago`;
};

const FILTERS: Array<{ value: "" | InvoiceStatus; label: string }> = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "expired", label: "Expired" },
  { value: "failed", label: "Failed" },
];

export function Activity() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Invoice[] | null>(null);
  const [filter, setFilter] = useState<"" | InvoiceStatus>("");
  const [error, setError] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState<ExportRange>("month");
  const [exportMode, setExportMode] = useState<"" | "live" | "test">("live");
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportErr(null);
    try {
      const { from, to } = rangeBounds(exportRange);
      await downloadInvoicesCsv({
        from,
        to,
        status: filter || undefined,
        mode: exportMode || undefined,
      });
      setExportOpen(false);
    } catch (e) {
      setExportErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    (async () => {
      try {
        const r = await listInvoicesAdmin({
          status: filter || undefined,
          limit: 100,
        });
        if (!cancelled) setItems(r.data);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <div className="scroll">
      <div className="pad fade-in">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div className="seg">
            {FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                type="button"
                className={`seg-pill${filter === f.value ? " active" : ""}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setExportOpen((o) => !o)}
              style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
            >
              <Ic name="download" size={15} /> Export CSV
            </button>
            {exportOpen && (
              <>
                <div
                  onClick={() => setExportOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 40 }}
                />
                <div
                  className="card"
                  style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 41, width: 264, padding: 14, boxShadow: "var(--sh-pop, 0 12px 40px -12px rgba(0,0,0,.3))" }}
                >
                  <div className="s-label" style={{ marginTop: 0 }}>Date range</div>
                  <select
                    className="input"
                    value={exportRange}
                    onChange={(e) => setExportRange(e.target.value as ExportRange)}
                  >
                    {(Object.keys(RANGE_LABELS) as ExportRange[]).map((r) => (
                      <option key={r} value={r}>{RANGE_LABELS[r]}</option>
                    ))}
                  </select>
                  <div className="s-label">Mode</div>
                  <div className="set-seg">
                    {([["live", "Live"], ["test", "Test"], ["", "Both"]] as const).map(([v, l]) => (
                      <button key={l} type="button" className={`set-seg-opt${exportMode === v ? " on" : ""}`} onClick={() => setExportMode(v)}>{l}</button>
                    ))}
                  </div>
                  <div className="help" style={{ margin: "8px 0 10px" }}>
                    {filter ? `Status: ${filter}. ` : "All statuses. "}UTC dates.
                  </div>
                  {exportErr && <div style={{ marginBottom: 8 }}><Banner tone="fail">{exportErr}</Banner></div>}
                  <Btn block onClick={() => void handleExport()} loading={exporting}>
                    <Ic name="download" size={15} /> Download
                  </Btn>
                </div>
              </>
            )}
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 14 }}>
            <Banner tone="fail">{error}</Banner>
          </div>
        )}

        <div className="card" style={{ marginTop: 14 }}>
          {items === null ? (
            <div className="card-pad" style={{ color: "var(--ink-3)" }}>
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div
                style={{
                  width: 64,
                  height: 64,
                  background: "var(--surface-2)",
                  borderRadius: 12,
                  margin: "0 auto 14px",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--ink-3)",
                }}
              >
                <Ic name="activity" size={28} />
              </div>
              <h4>No payment links</h4>
              <p>
                Create one from the Home tab — it'll show up here with its
                status, payment history, and the URL you shared.
              </p>
            </div>
          ) : (
            items.map((inv) => (
              <div
                key={inv.id}
                className="li"
                onClick={() =>
                  void navigate({
                    to: "/activity/$id",
                    params: { id: inv.id },
                  })
                }
                role="button"
                tabIndex={0}
              >
                <div
                  className={`li-ic ${
                    inv.status === "paid"
                      ? "ok"
                      : inv.status === "failed"
                        ? "fail"
                        : inv.status === "expired"
                          ? "muted"
                          : "warn"
                  }`}
                >
                  <Ic
                    name={
                      inv.status === "paid"
                        ? "check"
                        : inv.status === "failed"
                          ? "x"
                          : inv.status === "expired"
                            ? "clock"
                            : "link"
                    }
                    size={18}
                  />
                </div>
                <div className="li-main">
                  <div className="li-amt tnum">
                    ${(inv.amount_usd_cents / 100).toFixed(2)}
                  </div>
                  {inv.memo && <div className="li-memo">{inv.memo}</div>}
                  <div className="li-meta">{truncMid(inv.id)}</div>
                </div>
                <div className="li-right">
                  <Pill status={inv.status} />
                  <div className="li-time">
                    {formatRelative(inv.created_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
